/**
 * InputSpinner — input numérico con botones +/- y arrastre 2D
 *
 * Interacción:
 *   Arrastrar ◀▶ (horizontal) → cambia el valor con el paso activo
 *   Arrastrar ▲▼ (vertical)   → barre el popover de pasos; al soltar se cierra
 *   Clic en + / -              → incrementa / decrementa por el paso activo
 *
 * Props:
 *   value, onChange, min, max   — control del valor
 *   initialStep                 — paso inicial (default: 1)
 *   integer                     — fuerza pasos y valores enteros
 *   decimals                    — decimales visibles; si no se pasa, se deduce del paso
 *   stepOptions                 — pasos disponibles en el selector
 *   label                       — etiqueta opcional encima
 *   disabled                    — deshabilita el componente
 *   className                   — clases extra para el wrapper
 */
import React, { useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

const DEFAULT_STEPS = [0.1, 0.25, 1, 5, 10];

const countDecimals = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return 0;
    const text = number.toString().toLowerCase();
    if (text.includes('e-')) return Number(text.split('e-')[1]) || 0;
    return text.includes('.') ? text.split('.')[1].length : 0;
};

const clampDecimals = (value) => Math.min(Math.max(Number(value) || 0, 0), 8);

const formatNumber = (value, decimals) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    return Number(number.toFixed(clampDecimals(decimals))).toString();
};

export function InputSpinner({
    value,
    onChange,
    min = 0,
    max = Infinity,
    initialStep = 1,
    integer = false,
    decimals,
    stepOptions,
    label,
    disabled = false,
    className = '',
}) {
    const availableSteps = (stepOptions ?? DEFAULT_STEPS).filter((option) => (
        Number.isFinite(Number(option)) && Number(option) > 0 && (!integer || Number(option) >= 1)
    ));
    const fallbackSteps = integer ? [1, 5, 10] : DEFAULT_STEPS;
    const normalizedSteps = availableSteps.length ? availableSteps : fallbackSteps;
    const valueDecimals = integer
        ? 0
        : Number.isFinite(Number(decimals))
            ? Number(decimals)
            : Math.max(countDecimals(initialStep), ...normalizedSteps.map(countDecimals));
    const normalizeValue = useCallback((val) => {
        const number = Number(val);
        if (!Number.isFinite(number)) return min;
        const clamped = Math.min(Math.max(number, min), max);
        return integer ? Math.round(clamped) : Number(clamped.toFixed(clampDecimals(valueDecimals)));
    }, [integer, min, max, valueDecimals]);

    const normalizeStep = useCallback((val) => {
        const number = Number(val);
        if (!Number.isFinite(number) || number <= 0) return normalizedSteps[0];
        return integer ? Math.max(1, Math.round(number)) : number;
    }, [integer, normalizedSteps]);

    const [step, setStep] = useState(() => normalizeStep(initialStep));
    const [isDragging, setIsDragging] = useState(false);
    const [isAdjustingStep, setIsAdjustingStep] = useState(false);
    const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });

    const mouseMoved = useRef(false);
    const initialDirection = useRef(null);
    const startPosition = useRef({ x: 0, y: 0 });
    const startValue = useRef(0);
    const pendingValue = useRef(null);
    const changeFrame = useRef(null);
    // Refs a cada fila del popover para hit-test con getBoundingClientRect
    const stepRowRefs = useRef([]);

    const flushPendingChange = useCallback(() => {
        changeFrame.current = null;
        if (pendingValue.current === null) return;
        onChange(pendingValue.current);
        pendingValue.current = null;
    }, [onChange]);

    const scheduleChange = useCallback((nextValue) => {
        pendingValue.current = normalizeValue(nextValue);
        if (changeFrame.current !== null) return;
        changeFrame.current = requestAnimationFrame(flushPendingChange);
    }, [flushPendingChange, normalizeValue]);

    const cancelScheduledChange = useCallback(() => {
        if (changeFrame.current !== null) {
            cancelAnimationFrame(changeFrame.current);
            changeFrame.current = null;
        }
        if (pendingValue.current !== null) {
            onChange(pendingValue.current);
            pendingValue.current = null;
        }
    }, [onChange]);

    const handleStepChange = useCallback((nextStep) => {
        setStep(normalizeStep(nextStep));
    }, [normalizeStep]);

    const handleIncrement = () => { if (!disabled) onChange(normalizeValue(Number(value) + step)); };
    const handleDecrement = () => { if (!disabled) onChange(normalizeValue(Number(value) - step)); };

    // Dado un punto (x, y), devuelve el step cuya fila lo contiene, o null
    const stepAtY = useCallback((y) => {
        for (let i = 0; i < stepRowRefs.current.length; i++) {
            const el = stepRowRefs.current[i];
            if (!el) continue;
            const rect = el.getBoundingClientRect();
            if (y >= rect.top && y <= rect.bottom) return normalizedSteps[i];
        }
        return null;
    }, [normalizedSteps]);

    const beginDrag = (x, y) => {
        mouseMoved.current = false;
        initialDirection.current = null;
        startPosition.current = { x, y };
        startValue.current = normalizeValue(value);
        setIsDragging(true);
    };

    const detectDirection = (deltaX, deltaY) => {
        if (mouseMoved.current) return;
        initialDirection.current = Math.abs(deltaX) >= Math.abs(deltaY) ? 'horizontal' : 'vertical';
        mouseMoved.current = true;
    };

    // Mouse
    const handleMouseDown = (e) => {
        if (disabled) return;
        e.preventDefault();
        beginDrag(e.clientX, e.clientY);

        const onMove = (ev) => {
            const deltaX = ev.clientX - startPosition.current.x;
            const deltaY = ev.clientY - startPosition.current.y;
            detectDirection(deltaX, deltaY);

            if (initialDirection.current === 'vertical' && Math.abs(deltaY) > 6) {
                setPopoverPosition({ x: startPosition.current.x, y: startPosition.current.y });
                setIsAdjustingStep(true);
                const found = stepAtY(ev.clientY);
                if (found !== null) handleStepChange(found);
            } else if (initialDirection.current === 'horizontal') {
                setIsAdjustingStep(false);
                scheduleChange(startValue.current + Math.floor(deltaX / 10) * step);
            }
        };

        const onUp = () => {
            cancelScheduledChange();
            setIsDragging(false);
            setIsAdjustingStep(false);
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    // Touch
    const handleTouchStart = (e) => {
        if (disabled || e.touches.length !== 1) return;
        const t = e.touches[0];
        beginDrag(t.clientX, t.clientY);

        const onMove = (ev) => {
            const tt = ev.touches[0];
            const deltaX = tt.clientX - startPosition.current.x;
            const deltaY = tt.clientY - startPosition.current.y;
            detectDirection(deltaX, deltaY);

            if (initialDirection.current) ev.preventDefault();

            if (initialDirection.current === 'vertical' && Math.abs(deltaY) > 6) {
                setPopoverPosition({ x: startPosition.current.x, y: startPosition.current.y });
                setIsAdjustingStep(true);
                const found = stepAtY(tt.clientY);
                if (found !== null) handleStepChange(found);
            } else if (initialDirection.current === 'horizontal') {
                setIsAdjustingStep(false);
                scheduleChange(startValue.current + Math.floor(deltaX / 10) * step);
            }
        };

        const onEnd = () => {
            cancelScheduledChange();
            setIsDragging(false);
            setIsAdjustingStep(false);
            document.removeEventListener('touchmove', onMove);
            document.removeEventListener('touchend', onEnd);
        };

        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('touchend', onEnd);
    };

    // --- Popover (inline para acceder a stepRowRefs) ---
    const popover = isAdjustingStep
        ? ReactDOM.createPortal(
            <div
                className="fixed z-[99999] flex flex-col gap-1 p-2 rounded-lg shadow-xl bg-base-100 border border-base-300 select-none pointer-events-none"
                style={{
                    top: popoverPosition.y,
                    left: popoverPosition.x,
                    transform: 'translate(-50%, -50%)',
                }}
            >
                {normalizedSteps.map((opt, i) => (
                    <div
                        key={opt}
                        ref={(el) => { stepRowRefs.current[i] = el; }}
                        className={[
                            'px-4 py-1.5 rounded text-sm font-semibold text-center transition-all duration-75',
                            step === opt
                                ? 'bg-primary text-primary-content scale-105 shadow'
                                : 'text-base-content/50',
                        ].join(' ')}
                    >
                        ×{opt}
                    </div>
                ))}
            </div>,
            document.body
        )
        : null;

    return (
        <div className={`flex flex-col gap-1 w-full ${className}`}>
            {label && (
                <span className="text-xs text-base-content/60 uppercase tracking-widest">{label}</span>
            )}

            <div className="relative flex items-center w-full select-none">
                <button
                    className="btn btn-outline btn-sm rounded-r-none flex-none w-8 min-w-0 px-0"
                    onClick={handleDecrement}
                    disabled={disabled || value <= min}
                    tabIndex={-1}
                >
                    −
                </button>

                <span
                    className={[
                        'flex-1 text-sm text-center border-t border-b border-base-300',
                        'h-8 flex items-center justify-center gap-1',
                        'transition-colors duration-100',
                        disabled
                            ? 'opacity-50 cursor-not-allowed'
                            : isDragging
                                ? 'cursor-ew-resize bg-primary/10 border-primary/50 text-primary'
                                : 'cursor-ew-resize hover:bg-base-200',
                    ].join(' ')}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                    style={{ touchAction: 'none' }}
                    title="Arrastrar ◀▶ para cambiar · ▲▼ para ajustar paso"
                >
                    <svg viewBox="0 0 8 12" className="hidden sm:block w-1.5 h-3 fill-base-content/30 shrink-0">
                        <path d="M4 0L8 4H0zM4 12L0 8H8z"/>
                    </svg>
                    <span className="font-mono">{formatNumber(normalizeValue(value), valueDecimals)}</span>
                    <span className="hidden sm:inline text-[10px] text-base-content/40">×{formatNumber(step, valueDecimals)}</span>
                </span>

                <button
                    className="btn btn-outline btn-sm rounded-l-none flex-none w-8 min-w-0 px-0"
                    onClick={handleIncrement}
                    disabled={disabled || value >= max}
                    tabIndex={-1}
                >
                    +
                </button>
            </div>

            {popover}
        </div>
    );
}

export default InputSpinner;
